import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BookOpen,
  CheckCircle2,
  ChevronDown,
  FileText,
  Lock,
  NotebookPen,
  PlayCircle,
  Video,
} from 'lucide-react';
import UnitAccessTimer from '../components/common/UnitAccessTimer';
import useScrollToTop from '../hooks/useScrollToTop';
import { CourseDetails, useCourseApi } from '../hooks/useCourseApi';
import { isLessonComplete } from '../utils/lessonHelpers';
import CourseBookLink from '../components/course/CourseBookLink';

const isUnitAccessible = (
  unitIndex: number,
  enrollmentDate: string,
  isOpenToAll: boolean
): { accessible: boolean; message?: string; daysLeft?: number } => {
  if (isOpenToAll) return { accessible: true };
  if (!enrollmentDate) return { accessible: false, message: 'تاريخ التسجيل غير متوفر' };

  const enrollment = new Date(enrollmentDate);
  const now = new Date();
  const daysSinceEnrollment = Math.floor(
    (now.getTime() - enrollment.getTime()) / (1000 * 60 * 60 * 24)
  );

  const unitStartDay = unitIndex * 7;
  const unitEndDay = (unitIndex + 1) * 7;

  if (daysSinceEnrollment >= unitStartDay && daysSinceEnrollment < unitEndDay) {
    return { accessible: true, daysLeft: unitEndDay - daysSinceEnrollment };
  }

  if (daysSinceEnrollment < unitStartDay) {
    const daysUntilStart = unitStartDay - daysSinceEnrollment;
    return {
      accessible: false,
      message: `ستصبح هذه الوحدة متاحة خلال ${daysUntilStart} ${daysUntilStart === 1 ? 'يوم' : 'أيام'}`,
    };
  }

  return { accessible: false, message: 'انتهت فترة الوصول لهذه الوحدة' };
};

const getUnitAccessStatus = (
  unitIndex: number,
  enrollmentDate: string,
  isOpenToAll: boolean
) => {
  const status = isUnitAccessible(unitIndex, enrollmentDate, isOpenToAll);

  if (status.accessible && status.daysLeft !== undefined) {
    return {
      ...status,
      statusText: `متاحة (${status.daysLeft} ${status.daysLeft === 1 ? 'يوم متبقي' : 'أيام متبقية'})`,
      statusColor:
        status.daysLeft <= 2 ? 'text-amber-700' : 'text-emerald-700',
    };
  }

  if (status.accessible && isOpenToAll) {
    return {
      ...status,
      statusText: 'متاحة دائماً',
      statusColor: 'text-emerald-700',
    };
  }

  return {
    ...status,
    statusText: status.message || 'غير متاحة',
    statusColor: 'text-red-600',
  };
};

const CourseCurriculum: React.FC = () => {
  useScrollToTop();
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { fetchCourseDetails, isLoading } = useCourseApi();

  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [collapsedUnits, setCollapsedUnits] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const loadCourseDetails = useCallback(
    async (forceRefresh = false) => {
      if (!courseId) return;

      setError(null);
      try {
        const response = await fetchCourseDetails(courseId, forceRefresh);
        if (response?.success && response.data) {
          setCourseDetails(response.data);
        } else {
          setError('فشل في تحميل تفاصيل المنهج');
        }
      } catch {
        setError('حدث خطأ أثناء تحميل المنهج');
      } finally {
        setInitialLoading(false);
      }
    },
    [courseId, fetchCourseDetails]
  );

  useEffect(() => {
    if (courseId) {
      setInitialLoading(true);
      void loadCourseDetails(true);
    }
  }, [courseId, loadCourseDetails]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'course_data_updated' && event.newValue) {
        void loadCourseDetails(true);
        localStorage.removeItem('course_data_updated');
      }
    };
    const onFocus = () => {
      if (courseId && !document.hidden) {
        void loadCourseDetails(true);
      }
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, [courseId, loadCourseDetails]);

  const toggleUnit = (unitId: string) => {
    setCollapsedUnits((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]
    );
  };

  const handleLessonClick = (unitIndex: number, lessonIndex: number) => {
    if (!courseDetails) return;

    const unitAccess = isUnitAccessible(
      unitIndex,
      courseDetails.enrollmentDate,
      courseDetails.isOpenToAll
    );
    if (!unitAccess.accessible) {
      setError(unitAccess.message || 'هذه الوحدة غير متاحة حالياً');
      return;
    }

    const unit = courseDetails.units[unitIndex];
    const lesson = unit?.lessons?.[lessonIndex];
    if (!lesson) {
      setError('الدرس غير متوفر');
      return;
    }

    if (
      (lesson.type === 'Quiz' || lesson.type === 'Assignment') &&
      lesson.isQuizSubmitted
    ) {
      setError(
        lesson.type === 'Assignment'
          ? 'تم إرسال هذا الواجب مسبقاً'
          : 'تم إرسال هذا الاختبار مسبقاً'
      );
      return;
    }

    setError(null);
    navigate(`/course-player/${courseId}?unit=${unitIndex}&lesson=${lessonIndex}`);
  };

  const calculateProgress = () => {
    if (!courseDetails?.units?.length) return 0;

    let total = 0;
    let completed = 0;

    courseDetails.units.forEach((unit) => {
      unit.lessons.forEach((lesson) => {
        total++;
        if (isLessonComplete(lesson)) {
          completed++;
        }
      });
    });

    return total > 0 ? (completed / total) * 100 : 0;
  };

  if (initialLoading || (isLoading && !courseDetails)) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white via-emerald-50/30 to-white"
        dir="rtl"
      >
        <div className="text-center p-8">
          <div
            className="h-14 w-14 rounded-full border-2 border-emerald-100 border-t-emerald-600 border-r-amber-400 animate-spin mx-auto mb-6"
            aria-hidden
          />
          <h3 className="font-el-messiri text-xl font-bold text-slate-900 mb-2">
            جاري تحميل المنهج
          </h3>
          <p className="font-cairo text-slate-600">يرجى الانتظار...</p>
        </div>
      </div>
    );
  }

  if (error && !courseDetails) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white via-emerald-50/30 to-white px-4"
        dir="rtl"
      >
        <div className="text-center p-8 max-w-md rounded-3xl bg-white border border-emerald-100 shadow-lg">
          <h2 className="font-el-messiri text-2xl font-bold text-slate-900 mb-4">
            تعذر تحميل المنهج
          </h2>
          <p className="font-cairo text-slate-600 mb-6">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/enrolled-courses')}
            className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-bold text-white bg-gradient-to-r from-emerald-700 to-emerald-800 font-cairo"
          >
            العودة إلى مناهجي
          </button>
        </div>
      </div>
    );
  }

  if (!courseDetails) return null;

  const totalLessons = courseDetails.units.reduce(
    (total, unit) => total + unit.lessons.length,
    0
  );
  const progress = calculateProgress();

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-white via-emerald-50/30 to-white"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-white rounded-3xl border border-emerald-100 shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-6 sm:p-8 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center shrink-0 shadow-lg shadow-emerald-900/20">
              <BookOpen className="w-8 h-8 text-white" strokeWidth={1.75} aria-hidden />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-el-messiri text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                {courseDetails.courseName}
              </h1>
              <p className="font-cairo text-slate-600 mb-4">
                استكشف محتوى المنهج واختر الدرس الذي تريد دراسته
              </p>
              <div className="flex flex-wrap items-center gap-3 text-sm font-cairo text-slate-500">
                <span className="rounded-full bg-emerald-50 px-3 py-1 ring-1 ring-emerald-100">
                  {courseDetails.units.length} وحدات
                </span>
                <span className="rounded-full bg-emerald-50 px-3 py-1 ring-1 ring-emerald-100">
                  {totalLessons} دروس
                </span>
                <span className="rounded-full bg-amber-50 px-3 py-1 ring-1 ring-amber-100 text-amber-900">
                  {progress.toFixed(0)}% مكتمل
                </span>
              </div>
              <div className="mt-4 h-2 rounded-full bg-emerald-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-amber-500 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {!courseDetails.isOpenToAll &&
          courseDetails.units.map((unit, unitIndex) => {
            const unitAccess = getUnitAccessStatus(
              unitIndex,
              courseDetails.enrollmentDate,
              courseDetails.isOpenToAll
            );
            if (unitAccess.accessible && unitAccess.daysLeft) {
              return (
                <UnitAccessTimer
                  key={unit.id}
                  unitIndex={unitIndex}
                  enrollmentDate={courseDetails.enrollmentDate}
                  totalUnits={courseDetails.units.length}
                  className="mb-4"
                />
              );
            }
            return null;
          })}

        {error && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 font-cairo text-sm text-red-800">
            <span className="flex-1">{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800 shrink-0"
              aria-label="إغلاق"
            >
              ×
            </button>
          </div>
        )}

        <div className="space-y-5">
          <CourseBookLink courseDetails={courseDetails} className="mb-1" />

          {courseDetails.units.map((unit, unitIndex) => {
            const unitId = String(unit.id);
            const unitAccess = getUnitAccessStatus(
              unitIndex,
              courseDetails.enrollmentDate,
              courseDetails.isOpenToAll
            );
            const isUnitLocked = !unitAccess.accessible;
            const isCollapsed = collapsedUnits.includes(unitId);

            return (
              <div
                key={unit.id}
                className="bg-white rounded-2xl border border-emerald-100 shadow-[0_4px_20px_rgba(0,0,0,0.05)] overflow-hidden"
              >
                <button
                  type="button"
                  className={`w-full p-5 sm:p-6 flex items-center gap-4 text-right transition-colors ${
                    isUnitLocked
                      ? 'opacity-60 cursor-not-allowed bg-slate-50'
                      : 'hover:bg-emerald-50/50'
                  }`}
                  onClick={() => !isUnitLocked && toggleUnit(unitId)}
                  disabled={isUnitLocked}
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                      isUnitLocked
                        ? 'bg-slate-200'
                        : 'bg-gradient-to-br from-emerald-600 to-emerald-800 text-white shadow-md'
                    }`}
                  >
                    {isUnitLocked ? (
                      <Lock className="w-5 h-5 text-slate-500" aria-hidden />
                    ) : (
                      <span className="font-el-messiri text-lg font-bold">{unitIndex + 1}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`font-el-messiri text-lg sm:text-xl font-bold mb-1 ${
                        isUnitLocked ? 'text-slate-500' : 'text-slate-900'
                      }`}
                    >
                      {unit.unitName}
                    </h3>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className={`font-cairo text-sm font-medium ${unitAccess.statusColor}`}>
                        {unitAccess.statusText}
                      </span>
                      <span
                        className={`font-cairo text-sm ${
                          isUnitLocked ? 'text-slate-400' : 'text-slate-500'
                        }`}
                      >
                        {unit.lessons.length} دروس
                      </span>
                    </div>
                  </div>
                  {!isUnitLocked && (
                    <ChevronDown
                      className={`w-6 h-6 text-emerald-700 shrink-0 transition-transform ${
                        isCollapsed ? '' : 'rotate-180'
                      }`}
                      aria-hidden
                    />
                  )}
                </button>

                {!isUnitLocked && !isCollapsed && (
                  <div className="border-t border-emerald-100">
                    {unit.lessons.map((lesson, lessonIndex) => {
                      const isCompleted = isLessonComplete(lesson);
                      const isSubmittedLocked =
                        (lesson.type === 'Quiz' || lesson.type === 'Assignment') &&
                        lesson.isQuizSubmitted === true;
                      const isFileOpened =
                        lesson.type === 'File' && lesson.isFileOpened === true;

                      return (
                        <button
                          key={lesson.id}
                          type="button"
                          disabled={isSubmittedLocked}
                          onClick={() => handleLessonClick(unitIndex, lessonIndex)}
                          className={`w-full p-4 sm:px-6 flex items-center gap-4 text-right border-b border-emerald-50 last:border-b-0 transition-colors ${
                            isSubmittedLocked
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-emerald-50/40 cursor-pointer'
                          }`}
                        >
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                              isCompleted ? 'bg-emerald-100' : 'bg-slate-100'
                            }`}
                          >
                            {isCompleted ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-700" aria-hidden />
                            ) : lesson.type === 'Video' ? (
                              <PlayCircle className="w-5 h-5 text-slate-600" aria-hidden />
                            ) : lesson.type === 'File' ? (
                              <FileText className="w-5 h-5 text-red-600" aria-hidden />
                            ) : lesson.type === 'Assignment' ? (
                              <NotebookPen className="w-5 h-5 text-blue-600" aria-hidden />
                            ) : (
                              <FileText className="w-5 h-5 text-slate-600" aria-hidden />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-cairo font-semibold text-slate-900 mb-1">
                              {lesson.lessonName}
                            </h4>
                            <div className="flex flex-wrap items-center gap-2 text-sm font-cairo text-slate-500">
                              <span className="inline-flex items-center gap-1">
                                {lesson.type === 'Video' ? (
                                  <>
                                    <Video className="w-3.5 h-3.5" aria-hidden />
                                    درس فيديو
                                  </>
                                ) : lesson.type === 'File' ? (
                                  <>
                                    <FileText className="w-3.5 h-3.5" aria-hidden />
                                    ملفات
                                  </>
                                ) : lesson.type === 'Assignment' ? (
                                  <>
                                    <NotebookPen className="w-3.5 h-3.5" aria-hidden />
                                    واجب
                                  </>
                                ) : (
                                  <>
                                    <FileText className="w-3.5 h-3.5" aria-hidden />
                                    اختبار
                                  </>
                                )}
                              </span>
                              {isCompleted && (
                                <span className="text-emerald-700 font-medium">مكتمل</span>
                              )}
                              {isFileOpened && (
                                <span className="text-emerald-700 font-medium">تم الفتح</span>
                              )}
                              {isSubmittedLocked && (
                                <span className="text-blue-700 font-medium">تم الإرسال</span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CourseCurriculum;
